import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, map, shareReplay, tap, finalize, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  LoginRequest,
  LoginResponse,
  AdminUser,
} from '../../interfaces/api/auth.interface';
import { STORAGE_KEYS } from '../../constants/storage-keys.constants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private refreshRequest$?: Observable<string>;

  token = signal<string | null>(localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN));
  refreshToken = signal<string | null>(
    localStorage.getItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN),
  );
  user$ = signal<AdminUser | null>(this.getStoredUser());
  isAdmin = computed(() => this.user$()?.role.key === 'administrador');
  isLogged = computed(() => !!this.token() || !!this.refreshToken());

  login(payload: LoginRequest) {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/admin/auth/login`, payload)
      .pipe(
        tap((res) => {
          this.setSession(res);
        }),
      );
  }

  refreshSession(): Observable<string> {
    const refreshToken = this.refreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token disponible'));
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshRequest$ = this.http
      .post<LoginResponse>(`${environment.apiUrl}/admin/auth/refresh`, {
        refreshToken,
      })
      .pipe(
        tap((res) => this.setSession(res)),
        map((res) => res.accessToken),
        finalize(() => {
          this.refreshRequest$ = undefined;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  hasRefreshToken(): boolean {
    return !!this.refreshToken();
  }

  private getStoredUser(): AdminUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.AUTH_USER);
      return raw ? (JSON.parse(raw) as AdminUser) : null;
    } catch {
      return null;
    }
  }

  private setSession(session: LoginResponse): void {
    this.token.set(session.accessToken);
    this.refreshToken.set(session.refreshToken);
    this.user$.set(session.user);
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, session.accessToken);
    localStorage.setItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN, session.refreshToken);
    localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(session.user));
  }

  private clearSession(): void {
    this.token.set(null);
    this.refreshToken.set(null);
    this.user$.set(null);
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  }
}
